import React, { memo, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Functions as FunctionsIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { textMatches } from "../../table/smartFilters";
import { AVERAGE_ACTIONS } from "./tradeAverages";
import {
  describeFilters,
  FILTER_HINT,
  formatPrice,
  formatQty,
} from "./averagesShared";

const SLATE = {
  border: "#e2e8f0",
  headBg: "#f1f5f9",
  rowBorder: "#eef2f6",
  hover: "#f8fafc",
  text: "#0f172a",
  muted: "#64748b",
  faint: "#cbd5e1",
};

// Buying actions read green, selling actions red: a small dot, nothing loud.
const ACTION_DOT = {
  Buy: "#15803d",
  Cover: "#15803d",
  Sell: "#b91c1c",
  Short: "#b91c1c",
};

const numeric = { fontVariantNumeric: "tabular-nums" };

const headCellSx = {
  backgroundColor: SLATE.headBg,
  color: SLATE.muted,
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  borderBottom: `1px solid ${SLATE.border}`,
  py: 1,
};

const PriceCell = ({ row }) =>
  row ? (
    <Box sx={{ lineHeight: 1.25 }}>
      <Box sx={{ ...numeric, fontSize: "0.9rem", fontWeight: 600, color: SLATE.text }}>
        {formatPrice(row.avg_price)}
      </Box>
      <Box sx={{ ...numeric, fontSize: "0.7rem", color: SLATE.muted }}>
        {formatQty(row.quantity)}
      </Box>
    </Box>
  ) : (
    <Box sx={{ color: SLATE.faint }}>&ndash;</Box>
  );

// Memoized: there can be a thousand symbols, so it only re-renders when the
// data changes. Lookups go through a Map, not a scan per cell.
const AveragesTable = memo(function AveragesTable({ rows, symbols }) {
  const byKey = useMemo(
    () => new Map(rows.map((r) => [`${r.symbol}|${r.action}`, r])),
    [rows],
  );
  return (
    <Box
      sx={{
        overflow: "auto",
        maxHeight: 360,
        border: `1px solid ${SLATE.border}`,
        borderRadius: 2,
      }}
    >
      <Table size="small" stickyHeader sx={{ width: "100%", minWidth: 520 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>Security</TableCell>
            {AVERAGE_ACTIONS.map((action) => (
              <TableCell key={action} align="right" sx={headCellSx}>
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    backgroundColor: ACTION_DOT[action],
                    mr: 0.75,
                  }}
                />
                {action}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {symbols.map((symbol) => (
            <TableRow
              key={symbol}
              hover
              sx={{
                "&:nth-of-type(even)": { backgroundColor: SLATE.hover },
                "& td": {
                  borderBottom: `1px solid ${SLATE.rowBorder}`,
                  py: 0.5,
                },
                "&:last-child td": { borderBottom: 0 },
              }}
            >
              <TableCell
                sx={{ fontSize: "0.85rem", fontWeight: 600, color: SLATE.text, whiteSpace: "nowrap" }}
              >
                {symbol}
              </TableCell>
              {AVERAGE_ACTIONS.map((action) => (
                <TableCell key={action} align="right" sx={{ minWidth: 100 }}>
                  <PriceCell row={byKey.get(`${symbol}|${action}`)} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
});

const LoadingSkeleton = () => (
  <Box sx={{ mt: 1.5, minWidth: 520 }}>
    {[0, 1, 2, 3].map((i) => (
      <Skeleton key={i} variant="rounded" height={34} sx={{ mb: 0.75 }} />
    ))}
  </Box>
);

// Average price per security, shown directly on the page above the trades.
export default function SecurityAveragesPanel({
  averages,
  query,
  filters,
  onClose,
}) {
  const {
    canAverage,
    loading,
    error,
    rows,
    symbols,
    totalTrades,
    includedTrades,
    isStale,
    loaded,
    load,
  } = averages;
  const chips = useMemo(() => describeFilters(query, filters), [query, filters]);
  const [find, setFind] = useState("");
  const shownSymbols = useMemo(
    () => (find.trim() ? symbols.filter((s) => textMatches(s, find)) : symbols),
    [symbols, find],
  );
  // Rows from different filters are never shown under the current chips.
  const ready = !loading && !error && loaded && !isStale;
  const waiting = canAverage && !error && (loading || isStale || !loaded);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1.5,
        p: { xs: 1.5, sm: 2 },
        border: `1px solid ${SLATE.border}`,
        borderLeft: "4px solid #B0D9E7",
        borderRadius: 2,
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
        // Size to the table, not to the screen.
        width: "fit-content",
        maxWidth: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "10px",
            backgroundColor: "#e0f0f6",
            color: "#0f172a",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FunctionsIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h2"
            sx={{ fontSize: "1.05rem", fontWeight: 700, color: SLATE.text, lineHeight: 1.3 }}
          >
            Average price per security
          </Typography>
          <Typography
            component="div"
            sx={{ fontSize: "0.78rem", color: SLATE.muted, mt: 0.25, maxWidth: 520 }}
          >
            {ready
              ? `${formatQty(symbols.length)} securities · ${formatQty(includedTrades)} of ${formatQty(totalTrades)} matching trades · quantity weighted, across every page`
              : "Quantity weighted, across every page of matching trades"}
          </Typography>
        </Box>
        <Tooltip title="Recalculate">
          <span>
            <IconButton
              size="small"
              onClick={load}
              disabled={loading || !canAverage}
              aria-label="Recalculate averages"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Hide">
          <IconButton size="small" onClick={onClose} aria-label="Hide averages">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 1.25 }}>
        {chips.map((label) => (
          <Chip
            key={label}
            label={label}
            size="small"
            sx={{ backgroundColor: SLATE.headBg, color: SLATE.text, fontSize: "0.72rem" }}
          />
        ))}
      </Box>

      {!canAverage && (
        <Alert severity="info" sx={{ mt: 1.5, maxWidth: 520 }}>
          {FILTER_HINT}
        </Alert>
      )}

      {waiting && <LoadingSkeleton />}

      {canAverage && error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}

      {canAverage && ready && rows.length === 0 && (
        <Typography sx={{ mt: 1.5, fontSize: "0.85rem", color: SLATE.muted }}>
          No Buy, Sell, Short or Cover trades with a price in this result.
        </Typography>
      )}

      {canAverage && ready && rows.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          {symbols.length > 8 && (
            <TextField
              size="small"
              placeholder="Find a security"
              value={find}
              onChange={(e) => setFind(e.target.value)}
              sx={{ mb: 1, width: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
          {shownSymbols.length === 0 ? (
            <Typography sx={{ fontSize: "0.85rem", color: SLATE.muted }}>
              No security matches "{find}".
            </Typography>
          ) : (
            <AveragesTable rows={rows} symbols={shownSymbols} />
          )}
        </Box>
      )}
    </Paper>
  );
}
